import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom'
import { AppLayout } from '@/layouts/AppLayout'
import { ProtectedRoute } from './ProtectedRoute'
import { NotFound } from '@/components/common/Forbidden'
import { PageLoader } from '@/components/ui/Spinner'

// Auth
const LoginPage = lazy(() => import('@/modules/auth/LoginPage'))
const ForgotPasswordPage = lazy(() => import('@/modules/auth/ForgotPasswordPage'))

// Modules (code-split)
const FrontDeskPage = lazy(() => import('@/modules/front-desk/FrontDeskPage'))
const IntakeDetailPage = lazy(() => import('@/modules/front-desk/IntakeDetailPage'))
const DashboardPage = lazy(() => import('@/modules/dashboard/DashboardPage'))
const StakeholdersPage = lazy(() => import('@/modules/stakeholders/StakeholdersPage'))
const StakeholderDetailPage = lazy(() => import('@/modules/stakeholders/StakeholderDetailPage'))
const DirectoryPage = lazy(() => import('@/modules/directory/DirectoryPage'))
const BusinessDetailPage = lazy(() => import('@/modules/businesses/BusinessDetailPage'))
const OrganizationDetailPage = lazy(() => import('@/modules/organizations/OrganizationDetailPage'))
const SkillsPage = lazy(() => import('@/modules/skills/SkillsPage'))
const DepartmentsPage = lazy(() => import('@/modules/departments/DepartmentsPage'))
const DepartmentDetailPage = lazy(() => import('@/modules/departments/DepartmentDetailPage'))
const PoliticalHierarchyPage = lazy(() => import('@/modules/political/PoliticalHierarchyPage'))
const OpportunitiesPage = lazy(() => import('@/modules/opportunities/OpportunitiesPage'))
const OpportunityDetailPage = lazy(() => import('@/modules/opportunities/OpportunityDetailPage'))
const ProjectsPage = lazy(() => import('@/modules/projects/ProjectsPage'))
const GrievancesPage = lazy(() => import('@/modules/grievances/GrievancesPage'))
const GrievanceDetailPage = lazy(() => import('@/modules/grievances/GrievanceDetailPage'))
const MeetingsPage = lazy(() => import('@/modules/meetings/MeetingsPage'))
const MeetingDetailPage = lazy(() => import('@/modules/meetings/MeetingDetailPage'))
const CalendarPage = lazy(() => import('@/modules/calendar/CalendarPage'))
const DocumentsPage = lazy(() => import('@/modules/documents/DocumentsPage'))
const ReportsPage = lazy(() => import('@/modules/reports/ReportsPage'))
const CleanupPage = lazy(() => import('@/modules/cleanup/CleanupPage'))
const MapPage = lazy(() => import('@/modules/map/MapPage'))
const SearchPage = lazy(() => import('@/modules/search/SearchPage'))
const SettingsPage = lazy(() => import('@/modules/settings/SettingsPage'))
const ProfilePage = lazy(() => import('@/modules/settings/ProfilePage'))
// [Action Management module] — remove these 2 lines + the 2 route entries below to uninstall.
const ActionsPage = lazy(() => import('@/modules/action-management/ActionsPage'))
const ActionDetailPage = lazy(() => import('@/modules/action-management/ActionDetailPage'))

const protect = (element: JSX.Element, permission?: string) => (
  <ProtectedRoute permission={permission}>{element}</ProtectedRoute>
)

/** Preserve deep links from the old `/stakeholders/:id` path. */
function LegacyContactRedirect() {
  const { id } = useParams()
  return <Navigate to={`/contacts/${id}`} replace />
}

export const router = createBrowserRouter([
  { path: '/login', element: <Suspense fallback={<PageLoader />}><LoginPage /></Suspense> },
  { path: '/forgot-password', element: <Suspense fallback={<PageLoader />}><ForgotPasswordPage /></Suspense> },
  {
    path: '/',
    element: <AppLayout />,
    children: [
      { index: true, element: <Navigate to="/front-desk" replace /> },
      { path: 'dashboard', element: protect(<DashboardPage />, 'dashboard:view') },

      { path: 'front-desk', element: protect(<FrontDeskPage />, 'grievances:view') },
      { path: 'front-desk/:id', element: protect(<IntakeDetailPage />, 'grievances:view') },

      { path: 'contacts', element: protect(<StakeholdersPage />, 'stakeholders:view') },
      { path: 'contacts/:id', element: protect(<StakeholderDetailPage />, 'stakeholders:view') },
      // Legacy redirects — keep old /stakeholders bookmarks working
      { path: 'stakeholders', element: <Navigate to="/contacts" replace /> },
      { path: 'stakeholders/:id', element: <LegacyContactRedirect /> },

      { path: 'directory', element: protect(<DirectoryPage />, 'businesses:view') },
      { path: 'businesses/:id', element: protect(<BusinessDetailPage />, 'businesses:view') },
      { path: 'organizations/:id', element: protect(<OrganizationDetailPage />, 'organizations:view') },
      // Legacy list routes now live under the unified Directory
      { path: 'businesses', element: <Navigate to="/directory" replace /> },
      { path: 'organizations', element: <Navigate to="/directory" replace /> },

      { path: 'skills', element: protect(<SkillsPage />, 'skills:view') },
      { path: 'departments', element: protect(<DepartmentsPage />, 'departments:view') },
      { path: 'departments/:id', element: protect(<DepartmentDetailPage />, 'departments:view') },
      { path: 'political-hierarchy', element: protect(<PoliticalHierarchyPage />, 'political:view') },

      { path: 'opportunities', element: protect(<OpportunitiesPage />, 'opportunities:view') },
      { path: 'opportunities/:id', element: protect(<OpportunityDetailPage />, 'opportunities:view') },

      { path: 'projects', element: protect(<ProjectsPage />, 'projects:view') },

      { path: 'issues', element: protect(<GrievancesPage />, 'grievances:view') },
      { path: 'issues/:id', element: protect(<GrievanceDetailPage />, 'grievances:view') },

      { path: 'meetings', element: protect(<MeetingsPage />, 'meetings:view') },
      { path: 'meetings/:id', element: protect(<MeetingDetailPage />, 'meetings:view') },

      { path: 'calendar', element: protect(<CalendarPage />, 'calendar:view') },
      /* [Action Management module] — remove these 2 route entries to uninstall. */
      { path: 'actions', element: protect(<ActionsPage />, 'tasks:view') },
      { path: 'actions/:id', element: protect(<ActionDetailPage />, 'tasks:view') },
      { path: 'documents', element: protect(<DocumentsPage />, 'documents:view') },
      { path: 'reports', element: protect(<ReportsPage />, 'reports:view') },
      { path: 'cleanup', element: protect(<CleanupPage />, 'cleanup:view') },
      { path: 'maps', element: protect(<MapPage />, 'maps:view') },
      { path: 'search', element: protect(<SearchPage />, 'search:view') },

      { path: 'settings', element: protect(<SettingsPage />, 'settings:view') },
      { path: 'settings/profile', element: protect(<ProfilePage />) },

      { path: '*', element: <NotFound /> },
    ],
  },
])
